import React, { useState, useEffect, useCallback } from 'react';
import { HMSPrebuilt } from '@100mslive/roomkit-react';
import { useSearchParams } from 'react-router-dom';

const baseUrl = process.env.REACT_APP_BASE_URL;

function BroadcasterPage() {
  const [searchParams] = useSearchParams();
  const [meetingStatus, setMeetingStatus] = useState('checking'); // checking, ready, active, error
  const [roomCode, setRoomCode] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // Get parameters from URL
  const lang = searchParams.get('lang') || 'en';
  const meetingRef = searchParams.get('meetingRef');
  const urlRoomCode = searchParams.get('roomCode');

  useEffect(() => {
    if (meetingRef) {
      checkMeetingStatus();
    } else {
      setErrorMessage('Missing meeting reference. Please check your meeting link.');
      setMeetingStatus('error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meetingRef, roomCode, meetingStatus ]);

  const startMeeting = useCallback(async () => {
    
    if (!meetingRef) return;

    try {
      const response = await fetch(`${baseUrl}/api/coach/hms/start_meeting`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          lang: lang,
          slot_ref: meetingRef
        })
      });

      const data = await response.json();
      if (data.url) {
        // If response contains a URL, redirect to it
        // window.location.href = data.url;
        // return;
        setRoomCode(urlRoomCode);
        setMeetingStatus('active');
      }else if (data.message) {
        setErrorMessage(data.message);
        setMeetingStatus('error');
      }  else {
        setErrorMessage('Failed to get room code from backend');
        setMeetingStatus('error');
      }
    } catch (error) {
      console.error('Error starting meeting:', error);
      setErrorMessage('Failed to start meeting. Please try again later.');
      setMeetingStatus('error');
    }
  }, [meetingRef, lang, baseUrl, urlRoomCode]);

  const checkMeetingStatus = useCallback(async () => {
    if (!meetingRef) return;

    try {
      const response = await fetch(`${baseUrl}/api/hms/session/check_meeting_status`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          lang: lang,
          meetingRef: meetingRef
        })
      });

      const data = await response.json();

      

      if ((data.meetingstatus === 'started' || data.meetingstatus === 'waiting') && urlRoomCode) {
        startMeeting();
      } else if (data.meetingstatus === 'not_ready') {
        setMeetingStatus('not_ready');
      } else {
        setErrorMessage('Meeting is not available');
        setMeetingStatus('error');
      }
    } catch (error) {
      console.error('Error checking meeting status:', error);
      setErrorMessage('Failed to check meeting status. Please try again later.');
      setMeetingStatus('error');
    }
  }, [meetingRef, lang, startMeeting, urlRoomCode]);

  const handleLeave = useCallback(() => {
    console.log('Broadcaster left the room');
    // Redirect to specific URL after leaving the session
    const redirectUrl = `${baseUrl}/${lang}/coach/session/${meetingRef}`;
    console.log('Redirecting to:', redirectUrl);
    window.location.href = redirectUrl;
  }, [lang, meetingRef]);

  // Render checking state
  const renderCheckingState = () => (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      backgroundColor: '#0b0e15',
      fontFamily: 'Arial, sans-serif',
      padding: '20px'
    }}>
      <div style={{
        backgroundColor: '#1c1e24',
        padding: '40px',
        borderRadius: '12px',
        textAlign: 'center',
        maxWidth: '500px',
        width: '100%',
        border: '1px solid #444954'
      }}>
        <div style={{
          width: '60px',
          height: '60px',
          border: '4px solid #444954',
          borderTop: '4px solid #ffc107',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          margin: '0 auto 30px'
        }}></div>
        
        <h2 style={{
          color: '#ffffff',
          marginBottom: '20px',
          fontSize: '24px'
        }}>
          📡 Preparing Your Broadcast
        </h2>
        
        <p style={{
          color: '#a3a8b8',
          lineHeight: '1.6',
          marginBottom: '30px'
        }}>
          Checking meeting status and preparing your broadcast room...
        </p>
        
        <div style={{
          backgroundColor: '#272a31',
          padding: '15px',
          borderRadius: '8px',
          marginBottom: '20px',
          border: '1px solid #444954'
        }}>
          <small style={{ color: '#a3a8b8' }}>
            Meeting Reference: {meetingRef}
          </small>
        </div>
      </div>
      
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );

  // Render not ready state
  const renderNotReadyState = () => (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      backgroundColor: '#0b0e15',
      fontFamily: 'Arial, sans-serif',
      padding: '20px'
    }}>
      <div style={{
        backgroundColor: '#1c1e24',
        padding: '40px',
        borderRadius: '12px',
        textAlign: 'center',
        maxWidth: '500px',
        width: '100%',
        border: '1px solid #f39c12'
      }}>
        <div style={{
          fontSize: '48px',
          color: '#f39c12',
          marginBottom: '20px'
        }}>
          ⏰
        </div>
        
        <h2 style={{
          color: '#f39c12',
          marginBottom: '20px',
          fontSize: '24px'
        }}>
          Session Not Ready
        </h2>
        
        <p style={{
          color: '#a3a8b8',
          lineHeight: '1.6',
          marginBottom: '20px'
        }}>
          The meeting time hasn't started yet. You can join the session up to 10 minutes before the scheduled time.
        </p>
        
        <div style={{
          backgroundColor: '#272a31',
          padding: '15px',
          borderRadius: '8px',
          marginBottom: '30px',
          border: '1px solid #444954'
        }}>
          <small style={{ color: '#a3a8b8' }}>
            Meeting Reference: {meetingRef}
          </small>
        </div>

        <button
          onClick={checkMeetingStatus}
          style={{
            backgroundColor: '#f39c12',
            color: '#ffffff',
            border: 'none',
            padding: '12px 30px',
            borderRadius: '8px',
            fontSize: '16px',
            fontWeight: 'bold',
            cursor: 'pointer',
            transition: 'background-color 0.3s'
          }}
          onMouseEnter={(e) => e.target.style.backgroundColor = '#e67e22'}
          onMouseLeave={(e) => e.target.style.backgroundColor = '#f39c12'}
        >
          Check Again
        </button>
      </div>
    </div>
  );

  // Render error state
  const renderErrorState = () => (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      backgroundColor: '#0b0e15',
      fontFamily: 'Arial, sans-serif',
      padding: '20px'
    }}>
      <div style={{
        backgroundColor: '#1c1e24',
        padding: '40px',
        borderRadius: '12px',
        textAlign: 'center',
        maxWidth: '500px',
        width: '100%',
        border: '2px solid #dc3545'
      }}>
        <div style={{
          fontSize: '48px',
          color: '#dc3545',
          marginBottom: '20px'
        }}>
          ⚠️
        </div>
        
        <h2 style={{
          color: '#dc3545',
          marginBottom: '20px',
          fontSize: '24px'
        }}>
          Session Error
        </h2>
        
        <p style={{
          color: '#a3a8b8',
          lineHeight: '1.6',
          marginBottom: '30px'
        }}>
          {errorMessage || 'There was an error preparing your broadcast session.'}
        </p>
        
        <button
          onClick={() => {
            setMeetingStatus('checking');
            setErrorMessage('');
            checkMeetingStatus();
          }}
          style={{
            backgroundColor: '#ffc107',
            color: '#000',
            border: 'none',
            padding: '12px 30px',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '16px',
            marginRight: '10px',
            fontWeight: 'bold'
          }}
          onMouseOver={(e) => e.target.style.backgroundColor = '#e0a800'}
          onMouseOut={(e) => e.target.style.backgroundColor = '#ffc107'}
        >
          Try Again
        </button>
        
        <button
          onClick={() => window.location.href = baseUrl}
          style={{
            backgroundColor: '#6c757d',
            color: 'white',
            border: 'none',
            padding: '12px 30px',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '16px',
            fontWeight: 'bold'
          }}
          onMouseOver={(e) => e.target.style.backgroundColor = '#5a6268'}
          onMouseOut={(e) => e.target.style.backgroundColor = '#6c757d'}
        >
          Return Home
        </button>
      </div>
    </div>
  );

  // Main render logic
  if (meetingStatus === 'checking') {
    return renderCheckingState();
  }
  
  if (meetingStatus === 'not_ready') {
    return renderNotReadyState();
  }
  
  if (meetingStatus === 'error') {
    return renderErrorState();
  }
  
  if (meetingStatus === 'active' && roomCode) {
    return (
      <div style={{ width: '100%', height: '100vh' }}>
        <HMSPrebuilt 
          roomCode={roomCode}
          onLeave={handleLeave}
        />
      </div>
    );
  }

  // Fallback render
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      backgroundColor: '#0b0e15'
    }}>
      <div style={{ color: '#ffffff' }}>Loading...</div>
    </div>
  );
}

export default BroadcasterPage;
